/* LearnixTa Admin Panel — gated to ADMIN_EMAIL.
   4 sections: Live Numbers · Recent Conversations · Agent 2 Market Refresh · Agent 3 Insight Brief.
*/
import React, { useEffect, useState, useMemo } from 'react';
import { Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../lib/auth';
import {
  Activity, Users, Zap, RefreshCw, FileText, Database,
  ThumbsUp, ThumbsDown, AlertCircle, ArrowRight, CheckCircle2, Brain,
  Copy, AlertTriangle, Sparkles, Trophy, GraduationCap, MessageSquare,
} from 'lucide-react';
import AdminOutcomesPanel from '../components/AdminOutcomesPanel';
import AdminAfter10thSection from '../components/AdminAfter10thSection';
import AdminUserPreviewSection from '../components/AdminUserPreviewSection';
import LifeSkillsBrowser from '../components/lifeskills/LifeSkillsBrowser';
import AdminClarityCards from '../components/AdminClarityCards';
import AdminFeedback from '../components/AdminFeedback';
import AdminStories from '../components/AdminStories';
import AdminCommunity from '../components/AdminCommunity';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_EMAIL = (process.env.REACT_APP_ADMIN_EMAIL || 'learnixta@gmail.com').toLowerCase();

const Card = ({ title, value, sub, icon: Icon, accent = 'teal' }) => {
  const accents = {
    teal: 'from-teal-500 to-emerald-600',
    purple: 'from-purple-500 to-fuchsia-600',
    amber: 'from-amber-500 to-orange-600',
    rose: 'from-rose-500 to-pink-600',
    cyan: 'from-cyan-500 to-sky-600',
    slate: 'from-slate-600 to-gray-700',
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3 mb-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</p>
        <span className={`w-9 h-9 rounded-xl bg-gradient-to-br ${accents[accent]} text-white flex items-center justify-center shrink-0`}>
          <Icon size={16} />
        </span>
      </div>
      <p className="text-3xl font-black text-gray-900" data-testid={`admin-stat-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{value ?? '—'}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );
};

const Section = ({ title, icon: Icon, children, action }) => (
  <section className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
    <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-100 bg-gray-50">
      <div className="flex items-center gap-2.5">
        <span className="w-8 h-8 rounded-lg bg-gray-900 text-white flex items-center justify-center">
          <Icon size={15} />
        </span>
        <h2 className="font-bold text-gray-900">{title}</h2>
      </div>
      {action}
    </div>
    <div className="p-6">{children}</div>
  </section>
);

const LiveNumbers = ({ stats, loading, onRefresh }) => {
  if (loading || !stats) {
    return <div className="text-sm text-gray-500">Loading live numbers…</div>;
  }
  const helpful = stats.feedback_30d?.helpful_pct;
  const layerMap = Object.fromEntries((stats.by_layer_7d || []).map(x => [x._id, x.n]));
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card title="Chats today" value={stats.chats?.today} sub={`${stats.chats?.last_7d || 0} this week`} icon={Activity} accent="teal" />
        <Card title="Sessions (7d)" value={stats.unique_sessions_7d} sub={`${stats.chats?.last_30d || 0} chats in 30d`} icon={Zap} accent="cyan" />
        <Card title="Avg response" value={`${stats.avg_response_ms_7d || 0}ms`} sub={`Cache hits: ${layerMap.cache || 0}`} icon={Database} accent="purple" />
        <Card title="Helpful rate" value={helpful !== null ? `${helpful}%` : '—'} sub={`👍 ${stats.feedback_30d?.up || 0} · 👎 ${stats.feedback_30d?.down || 0}`} icon={ThumbsUp} accent="amber" />
        <Card title="Emotional chats" value={stats.emotional_chats_7d} sub="Routed to Sonnet 4.6" icon={AlertCircle} accent="rose" />
        <Card title="Total users" value={stats.users?.total} sub={`+${stats.users?.new_7d || 0} this week`} icon={Users} accent="slate" />
        <Card title="Quiz results" value={stats.quiz_results_total} sub="All-time saved" icon={FileText} accent="teal" />
        <Card title="Cache entries" value={stats.cache_entries} sub="30-day TTL" icon={Database} accent="cyan" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
        <div className="bg-gray-50 rounded-2xl p-4">
          <p className="text-xs font-bold text-gray-500 uppercase mb-2">Pathway split · 7d</p>
          <div className="space-y-1.5">
            {(stats.by_pathway_7d || []).map(p => (
              <div key={p._id || 'unknown'} className="flex items-center justify-between text-sm">
                <span className="text-gray-700">{p._id || 'unknown'}</span>
                <span className="font-bold text-gray-900">{p.n}</span>
              </div>
            ))}
            {(!stats.by_pathway_7d || stats.by_pathway_7d.length === 0) && <p className="text-xs text-gray-400 italic">No data yet</p>}
          </div>
        </div>
        <div className="bg-gray-50 rounded-2xl p-4">
          <p className="text-xs font-bold text-gray-500 uppercase mb-2">Model usage · 7d</p>
          <div className="space-y-1.5">
            {(stats.by_model_7d || []).map(m => (
              <div key={m._id} className="flex items-center justify-between text-sm">
                <span className="text-gray-700 font-mono text-xs">{m._id}</span>
                <span className="font-bold text-gray-900">{m.n}</span>
              </div>
            ))}
            {(!stats.by_model_7d || stats.by_model_7d.length === 0) && <p className="text-xs text-gray-400 italic">No LLM calls yet</p>}
          </div>
        </div>
      </div>
      <button onClick={onRefresh} className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-teal-700 hover:text-teal-900" data-testid="admin-refresh-stats">
        <RefreshCw size={12} /> Refresh numbers
      </button>
    </div>
  );
};

const Conversations = () => {
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    const params = { limit: 50 };
    if (filter === 'emotional') params.only_emotional = true;
    if (filter === 'thumbs-down') params.only_thumbs_down = true;
    try {
      const { data } = await axios.get(`${API}/admin/conversations`, { params, withCredentials: true });
      setConvs(data.conversations || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {[['all','All'],['emotional','Emotional only'],['thumbs-down','👎 only']].map(([v,l]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${filter===v ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            data-testid={`admin-conv-filter-${v}`}>{l}</button>
        ))}
      </div>
      {loading && <p className="text-sm text-gray-500">Loading…</p>}
      {!loading && convs.length === 0 && <p className="text-sm text-gray-400 italic">No conversations match the current filter.</p>}
      <div className="space-y-3 max-h-[520px] overflow-y-auto" data-testid="admin-conversations-list">
        {convs.map((c) => (
          <div key={c._id} className="border border-gray-200 rounded-xl p-3 bg-gray-50">
            <div className="flex items-center justify-between gap-2 mb-1.5 text-[10px] uppercase tracking-wide text-gray-500">
              <span>{(c.timestamp || '').replace('T',' ').slice(0,19)} · {c.layer_used} · {c.response_time_ms}ms</span>
              <div className="flex items-center gap-1.5">
                {c.emotional_flag && <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-bold">EMOTIONAL</span>}
                {c.feedback === 'up' && <ThumbsUp size={11} className="text-emerald-600" />}
                {c.feedback === 'down' && <ThumbsDown size={11} className="text-rose-600" />}
              </div>
            </div>
            <p className="text-sm font-semibold text-gray-900 mb-1">Q: {c.message}</p>
            <p className="text-xs text-gray-700 line-clamp-3 whitespace-pre-line">{c.response}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const MarketRefresh = () => {
  const [rolesText, setRolesText] = useState('Data Analyst\nProduct Manager\nFull Stack Developer\nUI/UX Designer\nDevOps Engineer');
  const [diff, setDiff] = useState(null);
  const [selected, setSelected] = useState({});
  const [running, setRunning] = useState(false);
  const [applied, setApplied] = useState(null);

  const runRefresh = async () => {
    const roles = rolesText.split('\n').map(s => s.trim()).filter(Boolean);
    if (!roles.length) return;
    setRunning(true); setDiff(null); setApplied(null);
    try {
      const { data } = await axios.post(`${API}/admin/market/refresh`, { roles }, { withCredentials: true });
      setDiff(data);
      const sel = {};
      (data.diff || []).forEach((d, i) => { if (d.changed) sel[i] = true; });
      setSelected(sel);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Refresh failed');
    } finally { setRunning(false); }
  };

  const apply = async () => {
    const changes = (diff?.diff || []).filter((_, i) => selected[i]);
    if (!changes.length) return;
    try {
      const { data } = await axios.post(`${API}/admin/market/apply`, { changes }, { withCredentials: true });
      setApplied(data.applied);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Apply failed');
    }
  };

  return (
    <div>
      <p className="text-sm text-gray-600 mb-3">Agent 2 — Claude Sonnet 4.6 pulls current Indian market data (job count · salary · AI risk) for each role and proposes a diff. You approve & apply.</p>
      <textarea
        value={rolesText}
        onChange={(e) => setRolesText(e.target.value)}
        placeholder="One role name per line…"
        rows={5}
        className="w-full text-sm border-2 border-gray-200 rounded-xl px-3 py-2 focus:border-teal-500 focus:outline-none mb-3 font-mono"
        data-testid="admin-market-roles-input"
      />
      <button onClick={runRefresh} disabled={running} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-purple-600 text-white text-sm font-bold hover:bg-purple-700 disabled:opacity-50" data-testid="admin-market-refresh-btn">
        <Brain size={14} /> {running ? 'Asking Sonnet 4.6…' : 'Generate market refresh'}
      </button>

      {diff && (
        <div className="mt-5">
          <p className="text-xs text-gray-500 mb-2">Generated at {(diff.generated_at || '').replace('T',' ').slice(0,19)} via {diff.model}</p>
          <table className="w-full text-sm border-collapse" data-testid="admin-market-diff-table">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                <th className="py-2 w-8"></th>
                <th className="py-2 pr-2">Role</th>
                <th className="py-2 pr-2">Job count</th>
                <th className="py-2 pr-2">Salary (LPA)</th>
                <th className="py-2 pr-2">AI risk</th>
              </tr>
            </thead>
            <tbody>
              {(diff.diff || []).map((d, i) => (
                <tr key={i} className={`border-b border-gray-100 ${d.changed ? 'bg-amber-50' : ''}`}>
                  <td className="py-2"><input type="checkbox" checked={!!selected[i]} onChange={(e) => setSelected(s => ({ ...s, [i]: e.target.checked }))} /></td>
                  <td className="py-2 pr-2 font-semibold">{d.role}</td>
                  <td className="py-2 pr-2">
                    {d.before.job_count && <span className="line-through text-gray-400 mr-1">{d.before.job_count}</span>}
                    <span className={d.changed ? 'font-bold text-gray-900' : 'text-gray-700'}>{d.after.job_count}</span>
                  </td>
                  <td className="py-2 pr-2">
                    {d.before.salary_range_lpa && <span className="line-through text-gray-400 mr-1">{d.before.salary_range_lpa}</span>}
                    <span className={d.changed ? 'font-bold text-gray-900' : 'text-gray-700'}>{d.after.salary_range_lpa}</span>
                  </td>
                  <td className="py-2 pr-2">
                    {d.before.ai_risk_level && <span className="line-through text-gray-400 mr-1">{d.before.ai_risk_level}</span>}
                    <span className={`font-bold ${d.after.ai_risk_level==='HIGH'?'text-rose-700':d.after.ai_risk_level==='MEDIUM'?'text-amber-700':'text-emerald-700'}`}>{d.after.ai_risk_level}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-4 flex items-center gap-3">
            <button onClick={apply} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700" data-testid="admin-market-apply-btn">
              <CheckCircle2 size={14} /> Apply {Object.values(selected).filter(Boolean).length} selected
            </button>
            {applied !== null && <span className="text-sm text-emerald-700 font-semibold">✓ Applied {applied} changes</span>}
          </div>
        </div>
      )}
    </div>
  );
};

const InsightBrief = () => {
  const [range, setRange] = useState('24h');
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (r) => {
    setLoading(true); setBrief(null);
    try {
      const { data } = await axios.get(`${API}/admin/brief?range=${r}`, { withCredentials: true });
      setBrief(data);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Brief failed');
    } finally { setLoading(false); }
  };

  return (
    <div>
      <p className="text-sm text-gray-600 mb-3">Agent 3 — Claude Sonnet 4.6 reads recent chat logs, quiz completions, and feedback to write the daily/weekly brief.</p>
      <div className="flex items-center gap-2 mb-4">
        {['24h','7d','30d'].map(r => (
          <button key={r} onClick={() => { setRange(r); load(r); }}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${range===r ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            data-testid={`admin-brief-range-${r}`}>Last {r}</button>
        ))}
      </div>
      {loading && <p className="text-sm text-gray-500">Sonnet is reading the logs…</p>}
      {brief && (
        <div className="bg-gradient-to-br from-purple-50 to-fuchsia-50 border border-purple-200 rounded-2xl p-5" data-testid="admin-brief-output">
          <div className="text-xs text-gray-500 mb-3">Generated {(brief.generated_at || '').replace('T',' ').slice(0,19)} · range {brief.range}</div>
          <article className="prose prose-sm max-w-none text-gray-800 whitespace-pre-line">
            {brief.brief_markdown}
          </article>
        </div>
      )}
      {!loading && !brief && <p className="text-sm text-gray-400 italic">Pick a range to generate a brief.</p>}
    </div>
  );
};

const WeakAnswers = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafting, setDrafting] = useState({});
  const [drafts, setDrafts] = useState({});
  const [copied, setCopied] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/weak-answers?limit=5&days=7`, { withCredentials: true });
      setItems(data.weak_answers || []);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const draft = async (i, item) => {
    setDrafting((s) => ({ ...s, [i]: true }));
    setDrafts((s) => ({ ...s, [i]: null }));
    try {
      const { data } = await axios.post(`${API}/admin/draft-template`, {
        question: item.question,
        bad_response: item.last_response,
        pathway_stage: item.pathway_stage,
      }, { withCredentials: true });
      setDrafts((s) => ({ ...s, [i]: data }));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Draft failed');
    } finally {
      setDrafting((s) => ({ ...s, [i]: false }));
    }
  };

  const copy = (i, text) => {
    navigator.clipboard?.writeText(text || '');
    setCopied((s) => ({ ...s, [i]: true }));
    setTimeout(() => setCopied((s) => ({ ...s, [i]: false })), 1800);
  };

  if (loading) return <p className="text-sm text-gray-500">Scanning the last 7 days of 👎 feedback…</p>;
  if (!items.length) {
    return (
      <div className="flex items-center gap-3 px-4 py-6 rounded-2xl bg-emerald-50 border border-emerald-200">
        <CheckCircle2 className="text-emerald-600 shrink-0" size={22} />
        <div>
          <p className="font-bold text-emerald-900 text-sm">No weak answers in the last 7 days.</p>
          <p className="text-xs text-emerald-800">Lexi is performing well — check back tomorrow.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">
        These questions got 👎 in the last 7 days. Click <b>Draft a Layer-1 template</b> and Sonnet 4.6 will write a stronger reply you can copy into <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">lexiTemplates.js</code>.
      </p>
      <div className="space-y-4" data-testid="admin-weak-answers-list">
        {items.map((it, i) => (
          <div key={i} className="border-2 border-amber-200 rounded-2xl p-4 bg-gradient-to-br from-amber-50/60 to-rose-50/40">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full">
                  <ThumbsDown size={11} /> {it.downvotes}× 👎
                </span>
                <span className="text-[11px] text-gray-500">{it.pathway_stage || 'unknown'} · {it.last_layer} · {(it.last_ts || '').replace('T',' ').slice(0,16)}</span>
              </div>
              <button
                onClick={() => draft(i, it)}
                disabled={drafting[i]}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-600 text-white text-xs font-bold hover:bg-purple-700 disabled:opacity-50 shrink-0"
                data-testid={`admin-draft-template-btn-${i}`}
              >
                <Sparkles size={11} /> {drafting[i] ? 'Sonnet drafting…' : 'Draft a Layer-1 template'}
              </button>
            </div>
            <p className="font-semibold text-gray-900 text-sm mb-2">Q: {it.question}</p>
            <details className="text-xs text-gray-600">
              <summary className="cursor-pointer text-gray-500 hover:text-gray-700">Lexi&apos;s previous reply ↓</summary>
              <p className="mt-2 whitespace-pre-line bg-white border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">{it.last_response}</p>
            </details>

            {drafts[i] && (
              <div className="mt-4 bg-white border-2 border-purple-300 rounded-xl p-4" data-testid={`admin-draft-output-${i}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div>
                    <p className="text-[11px] font-bold text-purple-700 uppercase tracking-wide">Sonnet 4.6 draft · template id: <code className="bg-purple-50 px-1 rounded">{drafts[i].template_id}</code></p>
                    <p className="text-[11px] text-gray-500 mt-0.5">{drafts[i].rationale}</p>
                  </div>
                  <button
                    onClick={() => copy(i, drafts[i].draft_markdown)}
                    className="inline-flex items-center gap-1 text-xs font-bold text-purple-700 hover:text-purple-900"
                    data-testid={`admin-copy-draft-${i}`}
                  >
                    <Copy size={12} /> {copied[i] ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="text-[11px] font-bold text-gray-500 uppercase mb-1.5">Trigger phrases</div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {(drafts[i].trigger_phrases || []).map((t, k) => (
                    <span key={k} className="text-[10px] bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full font-mono">{t}</span>
                  ))}
                </div>
                <div className="text-[11px] font-bold text-gray-500 uppercase mb-1.5">Draft markdown body</div>
                <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-800 whitespace-pre-wrap font-mono max-h-72 overflow-y-auto">{drafts[i].draft_markdown}</pre>
              </div>
            )}
          </div>
        ))}
      </div>
      <button onClick={load} className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-amber-700 hover:text-amber-900" data-testid="admin-weak-refresh">
        <RefreshCw size={12} /> Refresh
      </button>
    </div>
  );
};

const AdminPanel = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  const isAdmin = useMemo(() => (user?.email || '').toLowerCase() === ADMIN_EMAIL, [user]);

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const { data } = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(data);
    } catch (e) {
      console.error(e);
    } finally { setLoadingStats(false); }
  };

  useEffect(() => { if (isAdmin) loadStats(); }, [isAdmin]);

  if (user === null) return <div className="min-h-screen flex items-center justify-center text-gray-500">Checking your access…</div>;
  if (!user) return <Navigate to="/signin?next=/admin" replace />;
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white rounded-3xl border border-rose-200 p-8 max-w-lg text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Admin only</h1>
          <p className="text-gray-600 mb-4">This panel is restricted to <code className="text-xs">{ADMIN_EMAIL}</code>. You&apos;re signed in as {user.email}.</p>
          <Link to="/" className="inline-flex items-center gap-2 text-teal-700 font-semibold">← Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-gray-900 text-white py-5 px-6 sticky top-0 z-30 shadow-md">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold tracking-widest text-teal-300 uppercase">LearnixTa Admin</p>
            <h1 className="text-xl font-black">Lexi Operations Console</h1>
          </div>
          <Link to="/" className="text-xs text-gray-300 hover:text-white inline-flex items-center gap-1">Public site <ArrowRight size={11} /></Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        <Section title="Student Experience · Preview as User (God View)" icon={Sparkles}>
          <AdminUserPreviewSection />
        </Section>

        <Section title="Clarity Cards · every student's living career identity" icon={Users}>
          <AdminClarityCards />
        </Section>

        <Section title="Life Skills — For Every Future" icon={Sparkles}>
          <div className="rounded-2xl p-4 md:p-5" style={{ background: '#0B1F2E' }} data-testid="admin-lifeskills-section">
            <LifeSkillsBrowser />
          </div>
        </Section>

        <Section title="Live Numbers" icon={Activity}>
          <LiveNumbers stats={stats} loading={loadingStats} onRefresh={loadStats} />
        </Section>

        <Section title="Student Feedback · from the home page band" icon={MessageSquare}>
          <AdminFeedback />
        </Section>

        <Section title="Path Stories · verify & publish real journeys" icon={Users}>
          <AdminStories />
        </Section>

        <Section title="Community · today's questions, answers & private reports" icon={MessageSquare}>
          <AdminCommunity />
        </Section>

        <Section title="Weak Answer Alerts · last 7 days" icon={AlertTriangle}>
          <WeakAnswers />
        </Section>

        <Section title="Agent 2 · Market Refresh" icon={RefreshCw}>
          <MarketRefresh />
        </Section>

        <Section title="Agent 3 · Insight Brief" icon={FileText}>
          <InsightBrief />
        </Section>

        <Section title="After 10th · Group Quizzes" icon={GraduationCap}>
          <AdminAfter10thSection />
        </Section>

        <Section title="Outcomes Verification" icon={Trophy}>
          <AdminOutcomesPanel />
        </Section>

        <Section title="Recent Conversations" icon={Zap}>
          <Conversations />
        </Section>
      </main>
    </div>
  );
};

export default AdminPanel;
